import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSocket } from "../SocketContext";
import { useImpressaoConfig } from "../ImpressaoConfigContext";
import {
  lerCapacidadesTratativas,
  type ChaveTratativa,
} from "@/lib/tratativasImpressao";

// Descobre o que o módulo de impressão conectado sabe fazer.
//
// O módulo responde ao comando `ST` com `INFO|rede|senha|...|<versão>|...`.
// Firmware 2.26+ acrescenta no fim o campo `CAPTRAT>jg,pm,...` listando as
// tratativas de gravação que ele sabe ligar/desligar. Firmware antigo não
// manda esse campo — e é exatamente assim que a tela de preferências decide
// se libera ou não os seletores.
//
// O resultado fica em cache no localStorage por hardware, porque a tela de
// preferências costuma ser aberta com o módulo desligado: sem o cache, a
// pessoa veria tudo travado sem saber por quê.

type EstadoFirmware = {
  /** Versão reportada pelo módulo (ex.: "2.26" ou "2.26-ETH"). */
  versao: string;
  /** Chaves de tratativa que o firmware sabe controlar. Vazio = firmware
   *  antigo, que executa todas as tratativas de forma fixa. */
  capacidades: ChaveTratativa[];
  /** Momento da leitura (epoch ms), só para o texto de ajuda da tela. */
  lidoEm: number;
};

export type FirmwareContextType = {
  /** `null` enquanto nenhum módulo respondeu nem há cache. */
  firmware: EstadoFirmware | null;
  /** True quando o dado veio do cache e não desta sessão de socket. */
  vindoDoCache: boolean;
  /** True se o módulo respondeu ao ST nesta sessão. */
  moduloRespondeu: boolean;
  /** Pergunta o status ao módulo (ST). Sem efeito se o socket estiver fora. */
  atualizarInfoFirmware: () => void;
  /** Atalho: o firmware sabe controlar esta tratativa? */
  suportaTratativa: (chave: ChaveTratativa) => boolean;
};

const FirmwareContext = createContext<FirmwareContextType | undefined>(
  undefined,
);

const PREFIXO_CACHE = "inovacode:firmware-caps:";
// Evita rajada de ST quando várias telas pedem atualização ao mesmo tempo.
const INTERVALO_MINIMO_MS = 10_000;

function chaveDoCache(appId?: string) {
  return `${PREFIXO_CACHE}${appId || "desconhecido"}`;
}

function lerCache(appId?: string): EstadoFirmware | null {
  try {
    const bruto = localStorage.getItem(chaveDoCache(appId));
    if (!bruto) return null;
    const salvo = JSON.parse(bruto) as EstadoFirmware;
    if (!Array.isArray(salvo?.capacidades)) return null;
    return salvo;
  } catch {
    return null;
  }
}

function gravarCache(appId: string | undefined, estado: EstadoFirmware) {
  try {
    localStorage.setItem(chaveDoCache(appId), JSON.stringify(estado));
  } catch {
    // Modo privado / storage cheio: seguimos sem cache.
  }
}

export function FirmwareProvider({ children }: { children: ReactNode }) {
  const { lastMessage, enviarMensagem, isConnected } = useSocket();
  const { impressaoConfig } = useImpressaoConfig();
  const appId = impressaoConfig?.app_id;

  const [firmware, setFirmware] = useState<EstadoFirmware | null>(null);
  const [moduloRespondeu, setModuloRespondeu] = useState(false);
  const ultimoPedidoRef = useRef(0);

  // Troca de configuração (outro hardware) = outro cache.
  useEffect(() => {
    setFirmware(lerCache(appId));
    setModuloRespondeu(false);
  }, [appId]);

  const atualizarInfoFirmware = useCallback(() => {
    if (!isConnected) return;
    const agora = Date.now();
    if (agora - ultimoPedidoRef.current < INTERVALO_MINIMO_MS) return;
    ultimoPedidoRef.current = agora;
    enviarMensagem("ST");
  }, [isConnected, enviarMensagem]);

  // Ao conectar, pergunta uma vez. O ModuloDeImpressaoComponent ignora INFOs
  // que ele não pediu, então esse ST não abre diálogo nenhum na tela.
  useEffect(() => {
    if (!isConnected) return;
    const timer = setTimeout(atualizarInfoFirmware, 1000);
    return () => clearTimeout(timer);
  }, [isConnected, atualizarInfoFirmware]);

  useEffect(() => {
    const dados = lastMessage?.data;
    if (typeof dados !== "string" || !dados.includes("INFO|")) return;

    // Pode vir como "INFO|..." ou "Modulo: INFO|..." (mensagem espontânea
    // que o módulo manda ao conectar no socket).
    const partes = dados.split("|");
    const versao = partes[5]?.trim() ?? "";
    const capacidades = lerCapacidadesTratativas(partes);
    const estado: EstadoFirmware = { versao, capacidades, lidoEm: Date.now() };

    setFirmware(estado);
    setModuloRespondeu(true);
    gravarCache(appId, estado);
  }, [lastMessage, appId]);

  const suportaTratativa = useCallback(
    (chave: ChaveTratativa) => !!firmware?.capacidades.includes(chave),
    [firmware],
  );

  return (
    <FirmwareContext.Provider
      value={{
        firmware,
        vindoDoCache: !!firmware && !moduloRespondeu,
        moduloRespondeu,
        atualizarInfoFirmware,
        suportaTratativa,
      }}
    >
      {children}
    </FirmwareContext.Provider>
  );
}

export function useFirmware() {
  const context = useContext(FirmwareContext);
  if (!context) {
    throw new Error("useFirmware deve ser usado dentro de um FirmwareProvider");
  }
  return context;
}
